class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce(event => {
      this.onSubmitHandler(event);
    }, 500);
    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this)
    );
    this.btnClearFilters = this.querySelector('facet-remove')?.querySelector('a');
  }

  clearCheckboxes() {
    const checkboxes = this.querySelectorAll(
      'input[type="checkbox"]'
    );
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
  }

  static setListeners() {
    const onHistoryChange = event => {
      // Quando o drawer de filtros mobile está aberto, o popstate vem do botão
      // voltar fechando o drawer — não devemos re-renderizar aqui (isso desfaria
      // os filtros aplicados). O fechamento do drawer (MenuDrawer) cuida disso.
      if (FacetFiltersForm.suspendHistory) return;

      // Derivamos os filtros da própria URL (fonte da verdade), ignorando `page`
      // (paginação infinita / carregar mais) e `section_id`. Antes, líamos
      // event.state.searchParams, que era frágil: drawers (carrinho, menu, modal)
      // empilham/removem entradas sentinela com states diferentes, e o
      // collection-load-more gravava state vazio. Resultado: ao fechar o cart
      // drawer (que faz history.back()) numa coleção paginada, o facets via
      // searchParams=undefined e re-renderizava sem filtros, na página 1.
      // Comparando os filtros normalizados, um popstate que não altera os filtros
      // (só fecha um drawer) é corretamente ignorado, preservando grid e scroll.
      const target = FacetFiltersForm.normalizeSearchParams(window.location.search);
      const current = FacetFiltersForm.normalizeSearchParams(FacetFiltersForm.searchParamsPrev);

      if (target === current) return;
      FacetFiltersForm.renderPage(target, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static normalizeSearchParams(search) {
    const params = new URLSearchParams(search || '');
    params.delete('page');
    params.delete('section_id');
    params.sort();
    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(
          filterDataUrl,
          event
        )
        : FacetFiltersForm.renderSectionFromFetch(url, event);

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url }
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderCollectionView(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderCollectionView(html);
  }

  static renderCollectionView(html) {
    const collectionView = document.querySelector('collection-view');
    const collectionViewParent = collectionView?.parentElement;

    if (!collectionView) {
      return;
    }

    const updatedCollectionView = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('collection-view');

    if (!updatedCollectionView) {
      return;
    }

    const updatedCollectionViewParent = updatedCollectionView.parentElement;
    const isHidden = updatedCollectionViewParent.classList.contains('hidden');

    isHidden ? collectionViewParent.classList.add('hidden') : collectionViewParent.classList.remove('hidden');
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.querySelector('#ProductGridContainer');
    const collectionView = document.querySelector('collection-view');

    const updatedProductGridContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#ProductGridContainer');

    if (collectionView) {
      const btnActive = collectionView.querySelector('.js-btn:disabled');
      const updatedProductGrid = updatedProductGridContainer.querySelector('#product-grid');

      // btnActive só existe se algum botão de colunas estiver marcado como
      // ativo; sem o guarda, um render sem ele estourava aqui e o grid nunca
      // chegava a ser trocado.
      if (updatedProductGrid && btnActive) {
        updatedProductGrid.dataset.cols = btnActive.dataset.cols;
      }
    }

    productGridContainer.innerHTML = updatedProductGridContainer.innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount');
    const container = document.getElementById('ProductCount');

    if (container) {
      container.innerHTML = count ? count.innerHTML : '';
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    // A página renderiza os filtros duas vezes: no drawer (#FacetFiltersForm)
    // e na sidebar (#FacetFiltersFormAside). Cada cópia só pode ser atualizada
    // a partir da cópia equivalente: misturar as duas duplicaria ids e faria o
    // label da sidebar apontar para o checkbox do drawer.
    //
    // Dentro de cada formulário, o par é feito por `data-filter` (o param_name
    // do filtro, ex.: filter.p.m.custom.estampa) e não pela posição. A Shopify
    // remove da lista os grupos que ficaram sem valores aplicáveis depois de
    // filtrar, então o índice N do HTML novo pode ser outro filtro — era isso
    // que jogava os campos de preço dentro de "Estampa". `data-index` fica como
    // reserva para blocos que não são de filtro (o seletor de ordenação).
    const keyOf = element =>
      `${element.closest('form')?.id || ''}::${element.dataset.filter || element.dataset.index}`;

    // Quando o conjunto (ou a ordem) de grupos muda, trocar só o miolo de cada
    // grupo deixaria na tela grupos que não existem mais e esconderia os novos.
    // Nesse caso o accordion inteiro é substituído. Troca-se o outerHTML de
    // propósito: <accordion-default> guarda os <summary> no construtor, então
    // reaproveitar o elemento deixaria a lista com referências mortas.
    const structureChanged =
      FacetFiltersForm.syncFilterGroups(parsedHTML);

    const sources = new Map();

    parsedHTML.querySelectorAll('.js-filter').forEach(element => {
      sources.set(keyOf(element), element);
    });

    // Se o accordion foi trocado inteiro, o bloco onde o usuário clicou já não
    // está no documento — não há o que preservar.
    const activeFilter = event && !structureChanged
      ? event.target.closest('.js-filter')
      : null;

    // querySelectorAll, não querySelector: antes só a primeira cópia do
    // documento (a do drawer) era atualizada, e a sidebar ficava com as
    // contagens e os estados desabilitados congelados.
    document.querySelectorAll('.js-filter').forEach(target => {
      // O grupo em uso não é re-renderizado, para não recolher o accordion nem
      // tirar o foco do checkbox; ele é tratado por renderCounts.
      if (target === activeFilter) return;

      const source = sources.get(keyOf(target));
      if (source) target.innerHTML = source.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (activeFilter) {
      const activeSource = sources.get(keyOf(activeFilter));

      if (activeSource) {
        FacetFiltersForm.renderCounts(activeSource, activeFilter);
      }
    }

    if (structureChanged) {
      FacetFiltersForm.restoreFocusedFacet(event);
    }
  }

  // Devolve true se algum accordion precisou ser substituído por inteiro.
  static syncFilterGroups(parsedHTML) {
    const groupKeys = root =>
      Array.from(root.querySelectorAll('.js-filter'))
        .map(element => element.dataset.filter || element.dataset.index)
        .join('|');

    let changed = false;

    document.querySelectorAll('form.facets__form').forEach(form => {
      const target = form.querySelector('.facets__accordion');
      const sourceForm = form.id
        ? parsedHTML.getElementById(form.id)
        : null;
      const source = sourceForm?.querySelector('.facets__accordion');

      if (!target || !source) return;
      if (groupKeys(target) === groupKeys(source)) return;

      // O HTML novo vem com todos os grupos fechados, então a troca recolhia o
      // grupo em que o usuário acabou de clicar (ex.: marcar "Preto" em Cor faz
      // a Shopify deixar de devolver o grupo "Estampa", o que cai aqui).
      // Guardamos quais estavam abertos pela chave do filtro — não pela posição,
      // já que o conjunto de grupos é justamente o que mudou.
      const openKeys = FacetFiltersForm.openGroupKeys(target);

      target.outerHTML = source.outerHTML;
      changed = true;

      FacetFiltersForm.restoreOpenGroups(form, openKeys);
    });

    return changed;
  }

  // Mesma chave usada em renderFilters: o param_name do filtro, com data-index
  // como reserva para blocos que não são de filtro.
  static groupKeyOf(section) {
    const filter = section.querySelector('.js-filter');

    return filter
      ? filter.dataset.filter || filter.dataset.index
      : null;
  }

  static openGroupKeys(accordion) {
    const keys = new Set();

    accordion
      .querySelectorAll('.accordion__section')
      .forEach(section => {
        const isOpen =
          section.classList.contains('is-active') ||
          section.hasAttribute('open');
        const key = FacetFiltersForm.groupKeyOf(section);

        if (isOpen && key) keys.add(key);
      });

    return keys;
  }

  static restoreOpenGroups(form, openKeys) {
    if (!openKeys.size) return;

    form
      .querySelectorAll('.facets__accordion .accordion__section')
      .forEach(section => {
        const key = FacetFiltersForm.groupKeyOf(section);

        if (!key || !openKeys.has(key)) return;

        section.setAttribute('open', '');
        section.classList.add('is-active');
        section
          .querySelector('summary')
          ?.setAttribute('aria-expanded', 'true');

        // Estado final que o toggleDropdown do accordion deixa ao terminar a
        // animação. Sem a altura inline o corpo fica com o height: 0 do CSS.
        const body = section.querySelector('.accordion__body');
        if (body) body.style.height = 'auto';
      });
  }

  // Depois de trocar o accordion inteiro, o checkbox clicado é outro elemento e
  // o foco fica no <body> — o que quebra a navegação por teclado e faz o leitor
  // de tela perder o contexto. Reposicionamos o foco no equivalente do HTML
  // novo, casando por formulário + name + value.
  static restoreFocusedFacet(event) {
    const clicked = event?.target;

    if (!clicked || !clicked.name || clicked.value === undefined) return;
    if (document.activeElement && document.activeElement !== document.body) {
      return;
    }

    const formId = clicked.closest('form')?.id;
    const scope = formId ? document.getElementById(formId) : document;

    const replacement = scope?.querySelector(
      `input[name="${CSS.escape(clicked.name)}"][value="${CSS.escape(
        clicked.value
      )}"]`
    );

    replacement?.focus({ preventScroll: true });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelector = '.js-facets-active';
    const activeFacetsElement = html.querySelector(
      activeFacetElementSelector
    );

    if (!activeFacetsElement) return;
    document.querySelector(activeFacetElementSelector).outerHTML =
      activeFacetsElement.outerHTML;
  }

  static renderCounts(source, target) {
    const selector = '.js-facets-selected';
    const targetElement = target.querySelector(selector);
    const sourceElement = source.querySelector(selector);

    if (sourceElement && targetElement) {
      target.querySelector(selector).outerHTML =
        source.querySelector(selector).outerHTML;
    }

    FacetFiltersForm.renderFacetValueCounts(source, target);
  }

  // O grupo em que o usuário clicou não é re-renderizado (renderFilters o
  // exclui de propósito, para não recolher o accordion nem perder o foco).
  // Então aqui trocamos apenas o número e o estado desabilitado de cada opção,
  // casadas pelo value do input — os inputs em si continuam intactos.
  static renderFacetValueCounts(source, target) {
    const readItems = root => {
      const items = new Map();

      root.querySelectorAll('.facets__item').forEach(item => {
        const input = item.querySelector('input[type="checkbox"]');
        if (input) items.set(input.value, item);
      });

      return items;
    };

    const sourceItems = readItems(source);

    readItems(target).forEach((targetItem, value) => {
      const sourceItem = sourceItems.get(value);
      if (!sourceItem) return;

      const sourceCount = sourceItem.querySelector('[data-facet-count]');
      const targetCount = targetItem.querySelector('[data-facet-count]');

      if (sourceCount && targetCount) {
        targetCount.textContent = sourceCount.textContent;
      }

      // Sem isto, uma opção que zerou continuaria clicável mostrando "(0)".
      const sourceInput = sourceItem.querySelector(
        'input[type="checkbox"]'
      );
      const targetInput = targetItem.querySelector(
        'input[type="checkbox"]'
      );

      if (sourceInput && targetInput) {
        targetInput.disabled = sourceInput.disabled;
        targetItem
          .querySelector('.facet-checkbox')
          ?.classList.toggle(
            'checkbox--disabled',
            sourceInput.disabled
          );
      }
    });
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams && '?'.concat(searchParams)
      }`;

    // Enquanto o drawer de filtros (mobile) está aberto, usamos replaceState para
    // não empilhar uma entrada de history a cada filtro selecionado. Assim o botão
    // voltar fecha o drawer num único toque, sem desfazer cada seleção uma a uma.
    if (FacetFiltersForm.suspendHistory) {
      history.replaceState({ searchParams }, '', url);
    } else {
      history.pushState({ searchParams }, '', url);
    }
  }

  static getSections() {
    return [
      {
        section: document.querySelector('facet-filters-form').dataset
          .sectionId
      }
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const context = event.target.id.includes('desktop')
      ? 'mobile'
      : 'desktop';
    const formData = new FormData();
    const formDataElements = Array.from(
      event.target.closest('form').elements
    ).filter(
      element =>
        !element.id.includes(context) || element.name === 'sort_by'
    );
    let sortByValue = null;

    for (const element of formDataElements) {
      if (!element.name) continue;
      if (element.type === 'checkbox') {
        if (element.checked) {
          formData.append(element.name, element.value);
        }
      } else {
        formData.append(element.name, element.value);

        if (element.name === 'sort_by') {
          sortByValue = element.value;
        }
      }
    }
    // O <select name="sort_by"> vive apenas no formulário do topo
    // (#FacetFiltersForm). No estilo "sidebar", filtrar pela barra lateral
    // serializa o #FacetFiltersFormAside, que não tem esse campo — sem o
    // sort_by na query string o Shopify volta para a ordenação padrão, e a
    // ordem escolhida pelo cliente era perdida a cada filtro aplicado.
    // Buscamos o valor no documento quando o formulário que submeteu não o tem.
    if (!sortByValue) {
      const sortBySelect = document.querySelector('[name="sort_by"]');

      if (sortBySelect && sortBySelect.value) {
        sortByValue = sortBySelect.value;
        formData.append('sort_by', sortByValue);
      }
    }

    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);

    if (!sortByValue || !this.btnClearFilters) {
      return;
    }

    const sortParam = 'sort_by=';
    const sortValueIndex = this.btnClearFilters.href.indexOf(sortParam);
    let updatedBtnURL = this.btnClearFilters.href.slice(0, sortValueIndex + sortParam.length) + sortByValue;

    const currentSearch = new URLSearchParams(window.location.search);
    if (currentSearch.has('q')) {
      const qValue = currentSearch.get('q');
      if (!updatedBtnURL.includes('q=')) {
        updatedBtnURL = updatedBtnURL.replace('?', `?q=${qValue}&`);
      }
    }

    this.btnClearFilters.href = updatedBtnURL;
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
          event.currentTarget.href.indexOf('?') + 1
        );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// Flag controlada pelo MenuDrawer (global.js): true enquanto o drawer de filtros
// mobile está aberto. Ver updateURLHash / onHistoryChange.
FacetFiltersForm.suspendHistory = false;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = this.querySelectorAll('input');
    this.inputs.forEach(element =>
      element.addEventListener(
        'change',
        this.onRangeChange.bind(this)
      )
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', event => {
      event.preventDefault();
      const form =
        this.closest('facet-filters-form') ||
        document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
      form.clearCheckboxes();
    });
  }
}

customElements.define('facet-remove', FacetRemove);
